import React from 'react';
import {
  getSourceEntryName,
  getSourceEntryUrl,
  formatSourceFileSize,
  hasMeshSourceOnServer,
} from '../../../shared/lib/bkScanSourceFiles';

const FORMAT_OPTIONS = [
  { value: 'stl', label: 'STL' },
  { value: 'ply', label: 'PLY' },
  { value: 'obj', label: 'OBJ' },
];

const ScanUploadCard = ({
  selectedFormat,
  onFormatChange,
  selectedFileNames,
  uploadError,
  hideLocalUpload,
  hasServerSourceFiles,
  serverSourceFiles,
  isFetchingScanFiles,
  fileInputRef,
  onBrowseClick,
  onFileSelect,
  onRemoveFile,
  onClearAll,
  getAcceptAttribute,
}) => {
  const hasMeshOnServer = hasMeshSourceOnServer(serverSourceFiles);

  return (
    <div className="flex min-w-0 w-full flex-col rounded-lg border border-black/[0.08] bg-white p-[0.65rem] px-[0.85rem]">
      <h2 className="mb-[0.4rem] border-b border-black/[0.06] pb-[0.4rem] text-sm font-semibold tracking-tight text-[var(--text-ink,#171717)]">
        Upload Scan
      </h2>
      <p className="m-0 mb-2 text-[0.75rem] leading-snug text-[var(--text-secondary)]">
        {isFetchingScanFiles
          ? 'Checking for scan files…'
          : hideLocalUpload
            ? 'Scan linked to order — upload disabled here'
            : 'Upload before preparation'}
      </p>

      {hasServerSourceFiles && (
        <dl className="m-0 mb-2 flex max-h-[7rem] flex-col gap-0 overflow-y-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {serverSourceFiles.map((entry, idx) => {
            const name = getSourceEntryName(entry);
            const url = getSourceEntryUrl(entry);
            const sizeLabel = formatSourceFileSize(entry.size);
            return (
              <div
                key={`${name}-${idx}`}
                className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-2 border-b border-black/[0.06] py-[0.25rem] text-[0.75rem] last:border-b-0"
              >
                <dt
                  className="m-0 min-w-0 truncate font-medium text-[var(--text-primary)]"
                  title={name || undefined}
                >
                  {name || '—'}
                </dt>
                <dd className="m-0 shrink-0 whitespace-nowrap text-[var(--text-secondary)]">
                  {sizeLabel ? `${sizeLabel} · ` : null}
                  {url ? (
                    <a
                      className="font-semibold text-[var(--odp-purple)] no-underline hover:underline"
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open
                    </a>
                  ) : (
                    '—'
                  )}
                </dd>
              </div>
            );
          })}
        </dl>
      )}

      {hasServerSourceFiles && (
        <p className="m-0 mb-2 text-[0.6875rem] leading-snug text-[var(--text-secondary)]">
          {hasMeshOnServer
            ? 'Mesh on server — proceed to open workspace.'
            : 'No STL/PLY/OBJ — add mesh in BengaitLabs, then refresh.'}
        </p>
      )}

      {!hideLocalUpload && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <label className="sr-only" htmlFor="scan-format">
              File format
            </label>
            <select
              id="scan-format"
              value={selectedFormat}
              onChange={onFormatChange}
              className="min-w-0 flex-1 rounded-md border border-black/[0.12] bg-white px-2 py-1 text-[0.75rem] text-[var(--text-primary)] focus-visible:border-[var(--odp-purple)] focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_rgba(88,60,163,0.2)]"
            >
              {FORMAT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={onBrowseClick}
              className="cursor-pointer shrink-0 rounded-md border border-black/[0.12] bg-white px-2.5 py-1 text-[0.75rem] font-medium text-[var(--text-primary)] hover:bg-[#fafafa]"
            >
              Browse…
            </button>
          </div>

          {selectedFileNames.length > 0 && (
            <ul className="m-0 flex list-none flex-col gap-1 p-0">
              {selectedFileNames.map((fileName, index) => (
                <li
                  key={`${fileName}-${index}`}
                  className="flex items-center justify-between gap-2 rounded border border-black/[0.06] bg-[#fafafa] px-2 py-1 text-[0.6875rem]"
                >
                  <span className="min-w-0 flex-1 truncate" title={fileName}>
                    {fileName}
                  </span>
                  <button
                    type="button"
                    className="shrink-0 cursor-pointer border-0 bg-transparent p-0 text-[0.625rem] font-semibold text-red-800 hover:underline"
                    onClick={() => onRemoveFile(index)}
                    aria-label={`Remove ${fileName}`}
                  >
                    Remove
                  </button>
                </li>
              ))}
              <li className="list-none">
                <button
                  type="button"
                  className="cursor-pointer border-0 bg-transparent p-0 text-[0.6875rem] font-medium text-[var(--odp-purple)] hover:underline"
                  onClick={onClearAll}
                >
                  Clear all
                </button>
              </li>
            </ul>
          )}

          <input
            ref={fileInputRef}
            type="file"
            multiple={selectedFormat === 'obj'}
            accept={getAcceptAttribute()}
            onChange={onFileSelect}
            className="hidden"
          />
        </div>
      )}

      {uploadError && (
        <p className="m-0 mt-1.5 text-[0.6875rem] leading-snug text-red-700">{uploadError}</p>
      )}
    </div>
  );
};

export default ScanUploadCard;
